/**
 * score-calculator.ts - Utility functions for calculating player scores
 *
 * Handles score calculations for chess tournaments including:
 * - Regular wins/draws/losses
 * - Forfeits (single and double)
 * - Bye rounds
 */

import { RESULT_TYPES, SPECIAL_PLAYER_IDS, type ResultType } from './constants';
import type { Match } from '../../types/api';

interface ScoreMap {
  [playerId: string]: number;
}

interface MatchStats {
  wins: number;
  draws: number;
  losses: number;
}

/**
 * Calculate player scores before a given round
 * @param allMatches - All matches in the tournament
 * @param round - The round number to calculate scores before
 * @returns Object mapping player IDs to scores
 */
export function calculatePlayerScoresBeforeRound(allMatches: Match[], round: number): ScoreMap {
  const scores: ScoreMap = {};

  // Calculate scores from all matches before this round
  allMatches
    .filter((m): m is Match & { result: ResultType } => m.round < round && !!m.result)
    .forEach(match => {
      const player1Id = match.player1?.id || null;
      const player2Id = match.player2?.id || null;
      const isBye = match.isBye || player2Id === SPECIAL_PLAYER_IDS.BYE;

      // Initialize scores if not present
      if (player1Id && !scores[player1Id]) scores[player1Id] = 0;
      if (player2Id && player2Id !== SPECIAL_PLAYER_IDS.BYE && !scores[player2Id]) scores[player2Id] = 0;

      // Add points based on result (result is guaranteed to exist by filter)
      if (match.isBye && player1Id) {
        // Calculate score based on bye type (respects H, Z, U byes)
        let byeScore = 1; // Default for U type (uneven bye)
        if (match.result === RESULT_TYPES.DRAW) {
          byeScore = 0.5; // H type (half-point bye)
        } else if (match.result === RESULT_TYPES.BLACK_WIN) {
          byeScore = 0; // Z type (zero-point bye)
        }
        scores[player1Id]! += byeScore;
      } else if (match.result === RESULT_TYPES.WHITE_WIN || match.result === RESULT_TYPES.BLACK_FORFEIT) {
        if (player1Id) scores[player1Id]! += 1;
      } else if (match.result === RESULT_TYPES.BLACK_WIN || match.result === RESULT_TYPES.WHITE_FORFEIT) {
        if (player2Id && !isBye) scores[player2Id]! += 1;
      } else if (match.result === RESULT_TYPES.DRAW) {
        if (player1Id) scores[player1Id]! += 0.5;
        if (player2Id && !isBye) scores[player2Id]! += 0.5;
      } else if (match.result === RESULT_TYPES.DOUBLE_FORFEIT) {
        // Double forfeit - both players get 0 points (no action needed)
      }
    });

  return scores;
}

/**
 * Calculate total score for a single player
 * @param allMatches - All matches in the tournament
 * @param playerId - Player ID
 * @param upToRound - Calculate up to this round (inclusive). If not provided, calculates all rounds.
 * @returns Total score for the player
 */
export function calculatePlayerScore(allMatches: Match[], playerId: string, upToRound: number = Infinity): number {
  let score = 0;

  allMatches
    .filter((m): m is Match & { result: ResultType } => m.round <= upToRound && !!m.result)
    .forEach(match => {
      const player1Id = match.player1?.id || null;
      const player2Id = match.player2?.id || null;
      const isBye = match.isBye || player2Id === SPECIAL_PLAYER_IDS.BYE;

      if (player1Id === playerId) {
        if (match.isBye) {
          // Calculate score based on bye type (respects H, Z, U byes)
          let byeScore = 1; // Default for U type (uneven bye)
          if (match.result === RESULT_TYPES.DRAW) {
            byeScore = 0.5; // H type (half-point bye)
          } else if (match.result === RESULT_TYPES.BLACK_WIN) {
            byeScore = 0; // Z type (zero-point bye)
          }
          score += byeScore;
        } else if (match.result === RESULT_TYPES.WHITE_WIN || match.result === RESULT_TYPES.BLACK_FORFEIT) {
          score += 1;
        } else if (match.result === RESULT_TYPES.DRAW) {
          score += 0.5;
        }
      } else if (player2Id === playerId && !isBye) {
        if (match.result === RESULT_TYPES.BLACK_WIN || match.result === RESULT_TYPES.WHITE_FORFEIT) {
          score += 1;
        } else if (match.result === RESULT_TYPES.DRAW) {
          score += 0.5;
        }
      }
    });

  return score;
}

/**
 * Get match result statistics for a player
 * @param allMatches - All matches in the tournament
 * @param playerId - Player ID
 * @returns Object with wins, draws, losses counts
 */
export function getPlayerMatchStats(allMatches: Match[], playerId: string): MatchStats {
  const stats: MatchStats = { wins: 0, draws: 0, losses: 0 };

  allMatches
    .filter((m): m is Match & { result: ResultType } => !!m.result)
    .forEach(match => {
      const player1Id = match.player1?.id || null;
      const player2Id = match.player2?.id || null;
      const isBye = match.isBye || player2Id === SPECIAL_PLAYER_IDS.BYE;

      if (player1Id === playerId) {
        if (match.isBye || match.result === RESULT_TYPES.WHITE_WIN || match.result === RESULT_TYPES.BLACK_FORFEIT) {
          stats.wins += 1;
        } else if (match.result === RESULT_TYPES.DRAW) {
          stats.draws += 1;
        } else if (match.result === RESULT_TYPES.BLACK_WIN || match.result === RESULT_TYPES.WHITE_FORFEIT) {
          stats.losses += 1;
        }
      } else if (player2Id === playerId && !isBye) {
        if (match.result === RESULT_TYPES.BLACK_WIN || match.result === RESULT_TYPES.WHITE_FORFEIT) {
          stats.wins += 1;
        } else if (match.result === RESULT_TYPES.DRAW) {
          stats.draws += 1;
        } else if (match.result === RESULT_TYPES.WHITE_WIN || match.result === RESULT_TYPES.BLACK_FORFEIT) {
          stats.losses += 1;
        }
      }
    });

  return stats;
}
